// Faithful port of eb-ui `packages/attendee/social-share/src/components/ShareModal/ShareModal.tsx`.
//
// Bridge mapping (registry-verified, all peer react ^18||^19):
//   eds-copy-field  → @eventbrite/legacy-eds.copy-field   (identical API)
//   eds-icon-button → @eventbrite/legacy-eds.icon-button
//   eds-iconography → @eventbrite/legacy-eds.iconography  (ShareIosChunky)
//   eds-modal       → @eventbrite/legacy-eds.modal        (props used all exist)
//   eds-tooltip     → @eventbrite/legacy-eds.tooltip      (withTooltip HOC)
//   eds-utils       → @eventbrite/legacy-eds.utils/tracking (heap constants + retrack)
//   @eventbrite/i18n GenericLazyString → plain strings (local gettext shim;
//     GenericLazyString is unsafe to render under React 19). Prop types widen
//     to `string | { toString(): string }` so eb-ui-style lazy strings still
//     type-check at call sites.
//
// React 16 → 19: `children` is explicit on the prop types (no implicit
// children on React.FunctionComponent any more).
//
// ⚠ Trap C note (memory.md): the share trigger is an eds IconButton with an
// onClick ONLY — no `type="link"`/href pair anywhere in this tree, so there is
// no preventDefault contract to compensate; navigation is `openUrl()` inside
// the ShareBox handlers (preserved there).
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { CopyField } from '@eventbrite/legacy-eds.copy-field';
import { IconButton } from '@eventbrite/legacy-eds.icon-button';
import { ShareIosChunky } from '@eventbrite/legacy-eds.iconography';
import { Modal } from '@eventbrite/legacy-eds.modal';
import { withTooltip } from '@eventbrite/legacy-eds.tooltip';
import {
    HEAP_SHARE_EVENT_ACTION,
    HEAP_SHARE_EVENT_CLOSE_MODAL,
    HEAP_SHARE_EVENT_METHOD,
    reTrackHeapEvent,
} from '@eventbrite/legacy-eds.utils/tracking';
import { trackShareAction } from '../../tracking';

/** eb-ui passed `GenericLazyString`s here; any toString()-able value works. */
type LazyText = string | { toString(): string };

interface UseDialog {
    closeDialog: Function;
    isDialogOpen: boolean;
    openDialog: (event: React.MouseEvent) => void;
}

const useDialog = (initialMode = false): UseDialog => {
    const [isDialogOpen, setDialogOpen] = useState(initialMode);
    const openDialog = (): void => setDialogOpen(true);
    const closeDialog = (): void => setDialogOpen(false);

    return {
        closeDialog,
        isDialogOpen,
        openDialog,
    };
};

interface DataWebShareApi {
    text?: string;
    title?: string;
    url: string;
}

export interface Props {
    title: LazyText;
    inputLabel: LazyText;
    url: string;
    tooltipTitle: LazyText;
    tooltipText: LazyText;
    trackingCategory: string;
    isMobile: boolean;
    container: React.MutableRefObject<HTMLDivElement | null>;
    useWebShareApi?: boolean;
    dataWebShareApi?: DataWebShareApi;
    eventId?: string;
    affCode?: string | undefined;
    customShareIcon?: React.ReactNode;
    children?: React.ReactNode;
}

const shareUsingApi = async (
    dataWebShareApi: DataWebShareApi,
    trackingCategory: string,
) => {
    try {
        await navigator.share({ ...dataWebShareApi });
        trackShareAction({
            label: 'share-api-processed',
            category: trackingCategory,
        });
    } catch (err) {
        trackShareAction({
            label: 'share-api-cancelled',
            category: trackingCategory,
        });
    }
};

const openModal = async (
    e: React.MouseEvent,
    webShareApiEnabled: boolean,
    dataWebShareApi: DataWebShareApi | undefined,
    trackingCategory: string,
    openDialog: (e: React.MouseEvent) => void,
) => {
    if (webShareApiEnabled && dataWebShareApi?.url) {
        await shareUsingApi(dataWebShareApi, trackingCategory);
        trackShareAction({ label: 'share-api', category: trackingCategory });
    } else {
        openDialog(e);
        trackShareAction({ label: 'open-modal', category: trackingCategory });
    }
};

export const BaseShareModal: React.FunctionComponent<
    Pick<Props, 'container' | 'title' | 'inputLabel' | 'url'> & {
        isDialogOpen: boolean;
        closeDialog: Function;
        eventId?: string;
        affCode?: string | undefined;
        children?: React.ReactNode;
    }
> = ({
    isDialogOpen,
    container,
    title,
    closeDialog,
    inputLabel,
    url,
    children,
    eventId,
    affCode,
}) => {
    if (!isDialogOpen || container.current === null) {
        return null;
    }
    return ReactDOM.createPortal(
        <Modal
            title={title as string}
            onClose={() => {
                if (eventId) {
                    reTrackHeapEvent({
                        affCode,
                        eventId,
                        heapEventName: HEAP_SHARE_EVENT_CLOSE_MODAL,
                    });
                }
                closeDialog();
            }}
            isShown={isDialogOpen}
            noMinHeight={true}
            roleIsDialog={true}
        >
            <section className="eds-align--center eds-l-pad-vert-8">
                <div className="eds-g-cell-8-12">
                    <div className="eds-align--center eds-l-pad-bot-8">
                        {children}
                    </div>
                    <CopyField
                        inputFieldId="shareUrlField"
                        label={inputLabel as string}
                        value={url}
                        eventId={eventId}
                        onCopyClick={() => {
                            if (eventId) {
                                reTrackHeapEvent({
                                    affCode,
                                    eventId,
                                    heapEventName: HEAP_SHARE_EVENT_METHOD,
                                    shareMethod: 'copy',
                                });
                            }
                        }}
                    />
                </div>
            </section>
        </Modal>,
        container.current,
    );
};

type ButtonProps = {
    tooltipDisabled: boolean;
    tooltipTitle: LazyText;
    tooltipText: LazyText;
    eventId?: string;
    affCode?: string | undefined;
    onClick: (e: React.MouseEvent) => void;
    icon: React.ReactNode;
};

const Button: React.FunctionComponent<ButtonProps> = ({
    tooltipDisabled,
    tooltipTitle,
    tooltipText,
    onClick,
    icon = <ShareIosChunky />,
}) => {
    if (tooltipDisabled) {
        return (
            <IconButton
                iconType={icon}
                onClick={onClick}
                title={tooltipTitle}
            />
        );
    } else {
        const IconWithTooltip = withTooltip(IconButton);
        return (
            <IconWithTooltip
                iconType={icon}
                onClick={onClick}
                title={tooltipTitle}
                tooltipId="tooltip-share-modal-button"
                tooltipText={tooltipText}
                keepTooltipOpen={true}
                enhancedA11y={true}
            />
        );
    }
};

export const ShareModal: React.FunctionComponent<Props> = ({
    title,
    inputLabel,
    url,
    tooltipTitle,
    tooltipText,
    trackingCategory,
    isMobile,
    children,
    container,
    useWebShareApi,
    dataWebShareApi,
    eventId,
    affCode,
    customShareIcon,
}) => {
    const { openDialog, closeDialog, isDialogOpen } = useDialog();
    const [isTouchableDevice, setIsTouchableDevice] = useState(false);
    const [isWebShareApiEnabled, setIsWebShareApiEnabled] = useState(false);
    const [tooltipDisabled, setTooltipDisabled] = useState(false);

    useEffect(() => {
        setIsTouchableDevice(
            document.documentElement?.ontouchstart !== undefined,
        );
    }, []);

    useEffect(() => {
        setIsWebShareApiEnabled(
            isTouchableDevice && navigator.share !== undefined,
        );
    }, [isTouchableDevice]);

    useEffect(() => {
        setTooltipDisabled(useWebShareApi ? isTouchableDevice : isMobile);
    }, [useWebShareApi, isTouchableDevice, isMobile]);

    return (
        <>
            <Button
                tooltipDisabled={tooltipDisabled}
                tooltipTitle={tooltipTitle}
                tooltipText={tooltipText}
                eventId={eventId}
                icon={customShareIcon}
                onClick={(e: React.MouseEvent) => {
                    openModal(
                        e,
                        isWebShareApiEnabled,
                        dataWebShareApi,
                        trackingCategory,
                        openDialog,
                    );
                    if (eventId) {
                        reTrackHeapEvent({
                            affCode,
                            eventId,
                            heapEventName: HEAP_SHARE_EVENT_ACTION,
                        });
                    }
                }}
                affCode={affCode}
            />
            <BaseShareModal
                isDialogOpen={isDialogOpen}
                container={container}
                title={title}
                closeDialog={closeDialog}
                inputLabel={inputLabel}
                url={url}
                eventId={eventId}
                affCode={affCode}
            >
                {children}
            </BaseShareModal>
        </>
    );
};
